import { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';

type Mode = 'pencil' | 'eraser';

type WhiteboardProps = {
  mode: Mode;
  color: string;
  isEditing: boolean;
  visible: boolean;
  layerId: string;
  onActionPerformed?: (layerId: string, imageData: ImageData) => void;
};

type Point = {
  x: number;
  y: number;
};

export type WhiteboardRef = {
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  getSublayerInfo: () => { undoStack: string[], redoStack: string[] };
  drawGrid: (gridSize: number, color: string) => void;
};

const Whiteboard = forwardRef<WhiteboardRef, WhiteboardProps>(({ mode, color, isEditing, visible, layerId, onActionPerformed }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [prevPoint, setPrevPoint] = useState<Point | null>(null);
  const [resizeAnimFrame, setResizeAnimFrame] = useState<number>(-1);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [undoStack, setUndoStack] = useState<ImageData[]>([]);
  const [redoStack, setRedoStack] = useState<ImageData[]>([]);
  const maxHistorySize = 16;

  // FIXME feels hacky. This might be the right way to do so, but it feels... bad
  const [biggestImageData, _] = useState<{
    w: ImageData | null,
    h: ImageData | null,
  }>({
    w: null, h: null
  });

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    undo: () => {
      if (undoStack.length === 0 || !context || !canvasRef.current) return;

      // Get current state for redo
      const currentState = context.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);

      // Get previous state from undo stack
      const previousState = undoStack[undoStack.length - 1];

      // Apply previous state
      context.putImageData(previousState, 0, 0);

      // Update stacks
      setUndoStack(prev => prev.slice(0, -1));
      setRedoStack(prev => [...prev, currentState]);
    },
    redo: () => {
      if (redoStack.length === 0 || !context || !canvasRef.current) return;

      // Get current state for undo
      const currentState = context.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);

      // Get next state from redo stack
      const nextState = redoStack[redoStack.length - 1];

      // Apply next state
      context.putImageData(nextState, 0, 0);

      // Update stacks
      setRedoStack(prev => prev.slice(0, -1));
      setUndoStack(prev => [...prev, currentState]);
    },
    canUndo: () => undoStack.length > 0,
    canRedo: () => redoStack.length > 0,
    getSublayerInfo: () => {
      const generateThumbnail = (imageData: ImageData): string => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return '';

        // Create a small thumbnail (40x40)
        canvas.width = 40;
        canvas.height = 40;

        // Create a temporary canvas to resize the imageData
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) return '';

        tempCanvas.width = imageData.width;
        tempCanvas.height = imageData.height;
        tempCtx.putImageData(imageData, 0, 0);

        // Draw the resized image to the thumbnail canvas
        ctx.drawImage(tempCanvas, 0, 0, 40, 40);

        return canvas.toDataURL();
      };

      return {
        undoStack: undoStack.map(generateThumbnail),
        redoStack: redoStack.map(generateThumbnail)
      };
    },
         drawGrid: (gridSize: number, color: string) => {
       if (!context || !canvasRef.current) return;

       const canvas = canvasRef.current;
       const ctx = context;

       // Clear the canvas first
       ctx.clearRect(0, 0, canvas.width, canvas.height);

       // Set line style
       ctx.strokeStyle = color;
       ctx.lineWidth = 1;
       ctx.globalAlpha = 0.8;

       // Draw horizontal lines
       for (let y = 0; y <= canvas.height; y += gridSize) {
         ctx.beginPath();
         ctx.moveTo(0, y);
         ctx.lineTo(canvas.width, y);
         ctx.stroke();
       }

       // Draw vertical lines
       for (let x = 0; x <= canvas.width; x += gridSize) {
         ctx.beginPath();
         ctx.moveTo(x, 0);
         ctx.lineTo(x, canvas.height);
         ctx.stroke();
       }

       // Reset global alpha
       ctx.globalAlpha = 1.0;

       // Save current state to undo stack
       if (canvas) {
         const currentState = ctx.getImageData(0, 0, canvas.width, canvas.height);
         setUndoStack(prev => [...prev.slice(-maxHistorySize + 1), currentState]);
         setRedoStack([]);
         setHasUnsavedChanges(true);
       }
     }
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d', { alpha: true });
      if (ctx) {
        setContext(ctx);
        resizeCanvas(ctx);
      }
    }
  }, []);

  // Keyboard event handlers for undo/redo
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isEditing) return;
      
      if (event.ctrlKey || event.metaKey) {
        if (event.key === 'z' && !event.shiftKey) {
          event.preventDefault();
          if (undoStack.length > 0) {
            const currentState = context?.getImageData(0, 0, canvasRef.current!.width, canvasRef.current!.height);
            if (currentState) {
              const previousState = undoStack[undoStack.length - 1];
              context?.putImageData(previousState, 0, 0);
              setUndoStack(prev => prev.slice(0, -1));
              setRedoStack(prev => [...prev, currentState]);
            }
          }
        } else if ((event.key === 'y') || (event.key === 'z' && event.shiftKey)) {
          event.preventDefault();
          if (redoStack.length > 0) {
            const currentState = context?.getImageData(0, 0, canvasRef.current!.width, canvasRef.current!.height);
            if (currentState) {
              const nextState = redoStack[redoStack.length - 1];
              context?.putImageData(nextState, 0, 0);
              setRedoStack(prev => prev.slice(0, -1));
              setUndoStack(prev => [...prev, currentState]);
            }
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, undoStack, redoStack, context]);

  const resizeCanvas = (context: CanvasRenderingContext2D | null) => {
    if (resizeAnimFrame > 0) {
      cancelAnimationFrame(resizeAnimFrame);
    }

    setResizeAnimFrame(requestAnimationFrame(() => {
      setResizeAnimFrame(-1);
      console.log('resizeCanvas');
      if (canvasRef.current && context) {
        const canvas = canvasRef.current;

        let imageData: ImageData | null = null;
        if (canvas.width > 0 && canvas.height > 0) {
          imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        }

        const newCanvasWidth = (canvas.parentNode! as HTMLElement).offsetWidth; //Math.max(canvas.offsetWidth, originalCanvas.width / ratio);
        const newCanvasHeight = (canvas.parentNode! as HTMLElement).offsetHeight;
        console.log(newCanvasWidth, newCanvasHeight);

        if (newCanvasWidth > canvas.width) {
          // Extend the canvas size
          canvas.width = newCanvasWidth;
        }
        if (newCanvasHeight > canvas.height) {
          // Extend the canvas size
          canvas.height = newCanvasHeight;
        }

        if (imageData) {
          context.putImageData(imageData, 0, 0);
        }

        console.log('------------------------');
      }
    }));
  }

  useEffect(() => {
    const _resizeCanvas = () => resizeCanvas(context);
    window.addEventListener('resize', _resizeCanvas);
    return () => {
      window.removeEventListener('resize', _resizeCanvas);
    };
  }, [context]);

  const startDrawing = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isEditing) return;
    
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (canvas) {
      const { offsetX, offsetY } = event.nativeEvent;
      setPrevPoint({ x: offsetX, y: offsetY });
      setHasUnsavedChanges(true);
      
      // Capture the state BEFORE starting to draw
      if (canvasRef.current && context) {
        const imageData = context.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
        onActionPerformed?.(layerId, imageData);

        // Add to undo stack (this is the state BEFORE the current action)
        setUndoStack(prev => {
          const newStack = [...prev, imageData];
          if (newStack.length > maxHistorySize) {
            return newStack.slice(-maxHistorySize);
          }
          return newStack;
        });

        // Clear redo stack when new action is performed
        setRedoStack([]);
      }
    }
  };

  const endDrawing = () => {
    if (context && prevPoint) {
      if (mode === 'pencil') {
        context.beginPath();
        context.arc(prevPoint.x, prevPoint.y, 3, 0, 2 * Math.PI, false);
        context.fillStyle = color;
        context.fill();
      }
    }
    setIsDrawing(false);
    setPrevPoint(null);
    setHasUnsavedChanges(false);
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDrawing && context && prevPoint && canvasRef.current) {
      const canvas = canvasRef.current;
      const { offsetX, offsetY } = event.nativeEvent;
      
      // Clamp coordinates to canvas bounds
      const clampedX = Math.max(0, Math.min(canvas.width, offsetX));
      const clampedY = Math.max(0, Math.min(canvas.height, offsetY));
      
      if (mode === 'pencil') {
        // Draw the line to the clamped position
        context.strokeStyle = color;
        context.lineJoin = 'round';
        context.lineWidth = 6;
        context.beginPath();
        context.moveTo(prevPoint.x, prevPoint.y);
        context.lineTo(clampedX, clampedY);
        context.closePath();
        context.stroke();
        
        // Draw the final point
        context.beginPath();
        context.arc(clampedX, clampedY, 3, 0, 2 * Math.PI, false);
        context.fillStyle = color;
        context.fill();
      }
      
      setIsDrawing(false);
      setPrevPoint(null);
      setHasUnsavedChanges(false);
    }
  };

  const draw = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (context && isDrawing && prevPoint && canvas) {
      const { offsetX, offsetY } = event.nativeEvent;

      // Check if mouse is within 16px margin of canvas borders
      const margin = 16;
      const isWithinMargin = offsetX >= -margin &&
                            offsetX <= canvas.width + margin &&
                            offsetY >= -margin &&
                            offsetY <= canvas.height + margin;

      if (!isWithinMargin) {
        endDrawing();
        return;
      }

      if (mode === 'pencil') {
        context.strokeStyle = color;
        context.lineJoin = 'round';
        context.lineWidth = 6;
        context.beginPath();
        context.moveTo(prevPoint.x, prevPoint.y);
        context.lineTo(offsetX, offsetY);
        context.closePath();
        context.stroke();
        setPrevPoint({ x: offsetX, y: offsetY });
      }
      else {
        erase(prevPoint.x, prevPoint.y, offsetX, offsetY);
        setPrevPoint({ x: offsetX, y: offsetY });
      }
    }
  };

  const erase = (x1: number, y1: number, x2: number, y2: number) => {
    if (context && canvasRef.current) {
      const canvas = canvasRef.current;
      const dx = x2 - x1;
      const dy = y2 - y1;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const steps = Math.ceil(distance); // Adjust based on desired step size
      const eraserSize = 25; // Slightly smaller for better precision

      for (let i = 0; i < steps; i++) {
        const x = x1 + dx * (i / steps);
        const y = y1 + dy * (i / steps);

        // Get the area to erase
        const startX = Math.max(0, Math.floor(x - eraserSize / 2));
        const startY = Math.max(0, Math.floor(y - eraserSize / 2));
        const endX = Math.min(canvas.width, Math.ceil(x + eraserSize / 2));
        const endY = Math.min(canvas.height, Math.ceil(y + eraserSize / 2));

        // Get ImageData for the area
        const imageData = context.getImageData(startX, startY, endX - startX, endY - startY);
        const data = imageData.data;

        for (let j = 0; j < data.length; j += 4) {
          // Calculate distance from center for circular eraser
          const pixelX = (j / 4) % (endX - startX);
          const pixelY = Math.floor((j / 4) / (endX - startX));
          const centerX = x - startX;
          const centerY = y - startY;
          const pixelDistance = Math.sqrt((pixelX - centerX) ** 2 + (pixelY - centerY) ** 2);

          if (pixelDistance <= eraserSize / 2) {
            // Only make pixel transparent if it has some content (alpha > 0)
            if (data[j + 3] > 0) {
              data[j + 3] = 0; // Set alpha to 0 (transparent)
            }
          }
        }

        // Put the modified ImageData back
        context.putImageData(imageData, startX, startY);
      }
    }
  };

  return (
    <canvas
      className={`whiteboard-canvas ${isEditing ? 'editing' : ''} ${visible ? '' : 'hidden'}`}
      ref={canvasRef}
      style={{ border: '1px solid black' }}
      onMouseDown={startDrawing}
      onMouseUp={endDrawing}
      onMouseMove={draw}
      onMouseLeave={handleMouseLeave}
    />
  );
});

Whiteboard.displayName = 'Whiteboard';

export { Whiteboard };
